import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICES, OTHER_SERVICE, SUPPORT_REQUEST, PHONE_DISPLAY } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", business_name: "", email: "", phone: "", service: "", message: "", website: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const SERVICE_OPTIONS = [...SERVICES.map((s) => s.title), SUPPORT_REQUEST, OTHER_SERVICE];
const inputCls = "h-12 rounded-[10px] border-brand-surface bg-white text-brand-navy placeholder:text-[#64748B] focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-1";

const validate = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = "Please enter your full name.";
  else if (f.name.length > 100) e.name = "Name must be 100 characters or less.";
  if (!f.email.trim()) e.email = "Please enter your work email.";
  else if (!EMAIL_RE.test(f.email.trim())) e.email = "Please enter a valid email address.";
  if (f.business_name.length > 150) e.business_name = "Business name must be 150 characters or less.";
  if (f.phone.length > 30) e.phone = "Phone must be 30 characters or less.";
  if (!f.message.trim()) e.message = "Please tell us a little about what you need.";
  else if (f.message.length > 3000) e.message = "Message must be 3000 characters or less.";
  return e;
};

export const QuoteForm = ({ preselect, town }) => {
  const [form, setForm] = useState(() => ({ ...EMPTY, service: preselect?.service || "" }));
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");
  const preService = preselect?.service;
  const preTs = preselect?.ts;

  useEffect(() => {
    if (preService) {
      setForm((f) => ({ ...f, service: preService }));
      setStatus((s) => (s === "success" ? "idle" : s));
    }
  }, [preService, preTs]);

  const errors = validate(form);
  const show = (k) => touched[k] && errors[k];
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const blur = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, message: true, business_name: true, phone: true });
    if (Object.keys(errors).length) return;
    setStatus("submitting");
    setServerError("");
    const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim() || null]));
    if (town) payload.town = town;
    try {
      await axios.post(`${API}/quotes`, payload);
      setStatus("success");
      setForm(EMPTY);
      setTouched({});
    } catch (err) {
      const code = err.response?.status;
      setServerError(
        code === 429
          ? `Too many requests. Please try again later or call us at ${PHONE_DISPLAY}.`
          : code === 422
            ? "Please check the form fields and try again."
            : `Something went wrong sending your request. Please try again or call ${PHONE_DISPLAY}.`
      );
      setStatus("error");
    }
  };

  if (status === "success") return <SuccessCard onReset={() => setStatus("idle")} />;

  return (
    <form onSubmit={submit} noValidate data-testid="quote-form" className="soft-card relative p-6 shadow-[0_30px_60px_-40px_rgba(7,31,53,0.4)] sm:p-9">
      <h3 className="font-heading text-xl font-semibold text-brand-navy">Request a Quote</h3>
      <p className="mt-1.5 text-sm text-brand-slate">Fields marked * are required.</p>
      <div className="mt-7 grid gap-5 sm:grid-cols-2">
        <Field id="name" label="Full Name" required error={show("name")}>
          <Input id="name" value={form.name} onChange={set("name")} onBlur={blur("name")} maxLength={100} autoComplete="name" placeholder="Jane Smith" aria-invalid={!!show("name")} className={inputCls} data-testid="quote-name-input" />
        </Field>
        <Field id="business_name" label="Business Name" error={show("business_name")}>
          <Input id="business_name" value={form.business_name} onChange={set("business_name")} onBlur={blur("business_name")} maxLength={150} autoComplete="organization" placeholder="Your business" className={inputCls} data-testid="quote-business-input" />
        </Field>
        <Field id="email" label="Your work email" required error={show("email")}>
          <Input id="email" type="email" value={form.email} onChange={set("email")} onBlur={blur("email")} maxLength={254} autoComplete="email" placeholder="you@business.com" aria-invalid={!!show("email")} className={inputCls} data-testid="quote-email-input" />
        </Field>
        <Field id="phone" label="Phone" error={show("phone")}>
          <Input id="phone" type="tel" value={form.phone} onChange={set("phone")} onBlur={blur("phone")} maxLength={30} autoComplete="tel" placeholder="Your phone number" className={inputCls} data-testid="quote-phone-input" />
        </Field>
        <div className="sm:col-span-2">
          <Field id="service" label="Service">
            <Select value={form.service} onValueChange={(v) => v && setForm((f) => ({ ...f, service: v }))}>
              <SelectTrigger id="service" className={inputCls} data-testid="quote-service-select">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {SERVICE_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`quote-service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field id="message" label="Message" required error={show("message")}>
            <Textarea id="message" value={form.message} onChange={set("message")} onBlur={blur("message")} maxLength={3000} rows={5} placeholder="Tell us about your project or what you need help with..." aria-invalid={!!show("message")} className={`${inputCls} h-auto min-h-[130px] py-3`} data-testid="quote-message-input" />
          </Field>
        </div>
      </div>
      <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 opacity-0" data-testid="quote-honeypot" />
      {status === "error" && (
        <p role="alert" data-testid="quote-error-message" className="mt-5 flex items-start gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {serverError}
        </p>
      )}
      <button type="submit" disabled={status === "submitting"} data-testid="quote-submit-button" className="btn-primary mt-7 w-full sm:w-auto">
        {status === "submitting" ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : <>Request a Quote <ArrowRight className="h-4 w-4" /></>}
      </button>
    </form>
  );
};

const Field = ({ id, label, required, error, children }) => (
  <div>
    <label htmlFor={id} className="mb-2 block font-body text-[0.8rem] font-medium text-brand-navy">
      {label}{required && <span className="text-brand-blue"> *</span>}
    </label>
    {children}
    {error && <p className="mt-1.5 text-xs font-medium text-red-600" data-testid={`quote-${id}-error`}>{error}</p>}
  </div>
);

const SuccessCard = ({ onReset }) => (
  <div data-testid="quote-success" className="soft-card flex flex-col items-center justify-center p-10 text-center animate-in fade-in zoom-in-95 duration-300 sm:p-14">
    <span className="grid h-16 w-16 place-items-center rounded-full bg-brand-surface"><CheckCircle2 className="h-8 w-8 text-brand-blue" strokeWidth={1.6} /></span>
    <h3 className="mt-6 font-heading text-2xl font-semibold text-brand-navy" data-testid="quote-success-message">Thanks! We'll be in touch shortly.</h3>
    <p className="mt-3 max-w-sm text-sm text-brand-slate">Need help sooner? Call us at {PHONE_DISPLAY}.</p>
    <button type="button" onClick={onReset} className="link-arrow mt-7" data-testid="quote-send-another">
      Send another request <ArrowRight className="h-4 w-4" />
    </button>
  </div>
);
